import axiosInstance from '../../utils/axiosInstance';
import type { MapMarkerResponse } from '../../interfaces/Map/mapMarkers';
import type { Device } from './apiDevices';
import type { ShipmentResponse } from '../../interfaces/Shipment/Response/shipmentResponse';
import type { CompanyLocationsResponse } from '../../interfaces/Location/Response/companyLocations';

export type DashboardBootstrapInclude =
  | 'shipments'
  | 'devices'
  | 'alerts'
  | 'locations'
  | 'mapMarkers';

export interface DashboardBootstrapRequest {
  company_id: string;
  include: DashboardBootstrapInclude[];
  limit?: number;
  shipment_status?: 'CURRENT' | 'COMPLETED' | 'ARCHIVED';
  search?: string;
  map_scope?: 'client' | 'all';
  user_id?: string;
  role?: string;
  device_types?: string[];
  device_statuses?: string[];
}

interface SnapshotPagination {
  page?: number;
  page_size?: number;
  total_count?: number;
  total_pages?: number;
}

export interface SnapshotDevicesPayload {
  data?: Device[];
  pagination?: SnapshotPagination;
}

export interface SnapshotShipmentsPayload {
  data?: ShipmentResponse[];
  shipments?: ShipmentResponse[];
  items?: ShipmentResponse[];
}

export interface SnapshotLocationsPayload {
  locations?: CompanyLocationsResponse[];
  hasMore?: boolean;
}

export interface DashboardBootstrapData {
  company_id: string;
  include: string[];
  limit: number;
  partial: boolean;
  errors: Record<string, string>;
  shipments?: SnapshotShipmentsPayload | null;
  devices?: SnapshotDevicesPayload | null;
  alerts?: unknown;
  locations?: SnapshotLocationsPayload | CompanyLocationsResponse[] | null;
  map_markers?: MapMarkerResponse | null;
}

export interface DashboardBootstrapResponse {
  statusCode: number;
  data: DashboardBootstrapData;
  message: string;
  error: string | null;
}

const inFlight = new Map<string, Promise<DashboardBootstrapResponse>>();

const requestKey = (request: DashboardBootstrapRequest): string =>
  JSON.stringify({
    ...request,
    include: [...request.include].sort(),
    device_types: request.device_types ? [...request.device_types].sort() : undefined,
    device_statuses: request.device_statuses
      ? [...request.device_statuses].sort()
      : undefined,
  });

export const clearDashboardBootstrapCache = () => {
  inFlight.clear();
};

export const getDashboardBootstrap = async (
  request: DashboardBootstrapRequest,
): Promise<DashboardBootstrapResponse> => {
  const key = requestKey(request);
  const existing = inFlight.get(key);
  if (existing) return existing;

  const pending = axiosInstance
    .get<DashboardBootstrapResponse>('/mobile/fleet-snapshot', {
      params: {
        company_id: request.company_id,
        include: request.include.join(','),
        limit: request.limit,
        shipment_status: request.shipment_status,
        search: request.search,
        map_scope: request.map_scope,
        user_id: request.user_id,
        role: request.role,
        device_types: request.device_types?.join(','),
        device_statuses: request.device_statuses?.join(','),
      },
    })
    .then((response) => response.data)
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, pending);
  return pending;
};

